/**
 * GuestPortal — Hospitality > Guest Portal.
 *
 * Site-scoped captive-portal config, per SSID. Reuses the SSIDs screen's list+dialog
 * pattern and the Meridian design system (md-card, StatusPill, Button, chips) — a
 * scannable list of the site's SSIDs, each showing portal state, with an Edit dialog
 * for auth mode / walled garden / RADIUS. Saves fan out to the site's APs via owgw,
 * audited; the RADIUS secret is write-only.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useSession } from "@/stores/session";
import { usePortals, useSetPortal, useRadiusServers } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { RoleGate } from "@/components/auth/RoleGate";
import { HOST_LOCKED_HINT, REENTER_HINT, hostMoved } from "@/lib/radiusDestination";
import type { Portal } from "@/types/domain";

const MODE_LABEL: Record<string, string> = { click: "Click-through", radius: "RADIUS", voucher: "Voucher" };

/** A native color swatch + hex input pair, used across the branding fields. */
function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-ink-muted">{label}</span>
      <span className="flex items-center gap-1.5">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
          className="h-8 w-8 shrink-0 cursor-pointer rounded border border-line bg-surface p-0.5" />
        <input value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-xs text-ink outline-none focus:border-brand" />
      </span>
    </label>
  );
}

export default function GuestPortal() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = usePortals(siteId);
  const [editing, setEditing] = useState<Portal | null>(null);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Guest Portal" subtitle="Captive portal / splash" />
        <EmptyState
          title="Select a site first"
          hint="Guest portals apply to a site's Wi-Fi. Pick a site from the scope bar above."
        />
      </div>
    );
  }

  const portals = data?.portals ?? [];

  return (
    <div>
      <PageHeader title="Guest Portal" subtitle={siteName ? `Captive portal in ${siteName}` : undefined} />
      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading portals…</div>}

      {data && data.owgw_available === false && (
        <EmptyState title="No manageable APs" hint="No AP in this site has a live configuration." />
      )}
      {data && data.owgw_available && portals.length === 0 && (
        <EmptyState
          title="No SSIDs yet"
          hint="Create a Wi-Fi network under Wireless → SSIDs, then attach a guest portal to it here."
        />
      )}

      {portals.length > 0 && (
        <ul className="flex flex-col gap-2">
          {portals.map((p) => (
            <li key={p.ssid} className="md-card flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-ink">{p.ssid}</span>
                  {p.enabled ? (
                    <StatusPill status="healthy">
                      {MODE_LABEL[p.auth_mode ?? ""] ?? "On"}
                    </StatusPill>
                  ) : (
                    <span className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
                      Portal off
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-muted">
                  {p.enabled && p.walled_garden.length > 0 && (
                    <span>{p.walled_garden.length} walled-garden host{p.walled_garden.length === 1 ? "" : "s"}</span>
                  )}
                  {p.enabled && p.radius_host && <span className="font-mono">RADIUS {p.radius_host}</span>}
                  <span>{p.on_devices.length} AP{p.on_devices.length === 1 ? "" : "s"}</span>
                </div>
              </div>
              <RoleGate min="site_operator" mode="hide">
                <Button variant="secondary" onClick={() => setEditing(p)}>
                  {p.enabled ? "Edit portal" : "Set up portal"}
                </Button>
              </RoleGate>
            </li>
          ))}
        </ul>
      )}

      {editing && <PortalDialog siteId={siteId} portal={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PortalDialog({ siteId, portal, onClose }: { siteId: string; portal: Portal; onClose: () => void }) {
  const set = useSetPortal(siteId);
  const [enabled, setEnabled] = useState(portal.enabled);
  const [mode, setMode] = useState(portal.auth_mode ?? "click");
  const [garden, setGarden] = useState(portal.walled_garden.join("\n"));
  const [radiusHost, setRadiusHost] = useState(portal.radius_host ?? "");
  const [radiusPort, setRadiusPort] = useState(portal.radius_port != null ? String(portal.radius_port) : "1812");
  const [radiusSecret, setRadiusSecret] = useState("");
  // BYO RADIUS: "" = enter manually, otherwise a registered server's id.
  const [serverId, setServerId] = useState<string>("");
  const { data: rsData } = useRadiusServers();
  const servers = rsData?.radius_servers ?? [];

  // Splash branding (rendered into the captive web-root). Seed from stored branding or defaults.
  const b0 = portal.branding ?? {};
  const [headline, setHeadline] = useState(b0.headline ?? "Welcome");
  const [subhead, setSubhead] = useState(b0.subhead ?? "Sign in to connect to the network.");
  const [logo, setLogo] = useState(b0.logo ?? "");
  const [outerBg, setOuterBg] = useState(b0.outer_bg ?? "#5B4BE0");
  const [headlineColor, setHeadlineColor] = useState(b0.headline_color ?? "#ffffff");
  const [textColor, setTextColor] = useState(b0.text_color ?? "#1a1b28");
  const [buttonColor, setButtonColor] = useState(b0.button_color ?? "#1f2a44");
  const [termsText, setTermsText] = useState(b0.terms_text ?? "I agree to the Terms of Service.");
  const [redirectUrl, setRedirectUrl] = useState(b0.redirect_url ?? "");
  const [idleTimeout, setIdleTimeout] = useState(portal.idle_timeout != null ? String(portal.idle_timeout) : "");
  const [sessionTimeout, setSessionTimeout] = useState(portal.session_timeout != null ? String(portal.session_timeout) : "");

  const isRadius = enabled && mode === "radius";
  const usingServer = isRadius && serverId !== "";
  // RADIUS "secret follows the host" (D-18 follow-up; backend services/radius_destination):
  // the stored secret may only keep going to its own host. A site manager's host field is
  // locked while a secret is stored (a registered server can still be picked); an admin who
  // changes the host must re-enter the secret. The server enforces the same rule (403 / 400).
  const canMoveRadius = useSession((s) => s.hasRole("org_admin"));
  const hostLocked = !!portal.has_radius_secret && !canMoveRadius;
  const authMoved = hostMoved(portal.radius_host, radiusHost, portal.has_radius_secret);
  const secretRequired = isRadius && !usingServer && (!portal.has_radius_secret || authMoved);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isRadius && !usingServer && !radiusHost.trim()) return;
    if (secretRequired && !radiusSecret) return;
    const gardens = garden.split(/[\s,]+/).map((h) => h.trim()).filter(Boolean);
    set.mutate(
      {
        ssid: portal.ssid,
        enabled,
        ...(enabled
          ? {
              auth_mode: mode,
              walled_garden: gardens,
              ...(mode === "radius"
                ? usingServer
                  ? { radius_server_id: Number(serverId) }
                  : {
                      radius_host: radiusHost.trim(),
                      radius_port: radiusPort.trim() ? Number(radiusPort) : 1812,
                      ...(radiusSecret ? { radius_secret: radiusSecret } : {}),
                    }
                : {}),
              branding: {
                headline, subhead, logo: logo.trim(),
                outer_bg: outerBg, headline_color: headlineColor,
                text_color: textColor, button_color: buttonColor,
                terms_text: termsText, redirect_url: redirectUrl.trim(),
              },
              ...(idleTimeout.trim() ? { idle_timeout: Number(idleTimeout) } : {}),
              ...(sessionTimeout.trim() ? { session_timeout: Number(sessionTimeout) } : {}),
            }
          : {}),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[88vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Guest portal — {portal.ssid}</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Applies to every AP in this site broadcasting this SSID.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>Enable captive portal (splash)</span>
            </label>

            {enabled && (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Authentication</span>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
                  >
                    <option value="click">Click-through (accept terms)</option>
                    <option value="voucher">Voucher (guest access codes)</option>
                    <option value="radius">RADIUS</option>
                  </select>
                </label>

                {mode === "voucher" && (
                  <p className="rounded border border-line bg-surface-2 px-3 py-2 text-xs text-ink-muted">
                    Guests sign in with a code. Manage codes under Hospitality → Vouchers; the
                    active codes for this site are applied automatically.
                  </p>
                )}

                {isRadius && (
                  <div className="rounded border border-line bg-surface-2 p-3">
                    <div className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                      RADIUS server
                    </div>
                    {servers.length > 0 && (
                      <label className="mb-2 flex flex-col gap-1 text-sm">
                        <span className="text-ink-muted">Use a registered server</span>
                        <select
                          value={serverId}
                          onChange={(e) => setServerId(e.target.value)}
                          className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
                        >
                          <option value="">Enter manually…</option>
                          {servers.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name} ({s.auth_host})
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {!usingServer && (<>
                    <div className="grid grid-cols-3 gap-2">
                      <label className="col-span-2 flex flex-col gap-1 text-sm">
                        <span className="text-ink-muted">Host</span>
                        <input
                          value={radiusHost}
                          readOnly={hostLocked}
                          title={hostLocked ? HOST_LOCKED_HINT : undefined}
                          onChange={(e) => setRadiusHost(e.target.value)}
                          placeholder="10.0.0.9"
                          className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand read-only:opacity-70"
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-ink-muted">Port</span>
                        <input
                          inputMode="numeric"
                          value={radiusPort}
                          onChange={(e) => setRadiusPort(e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="1812"
                          className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                        />
                      </label>
                    </div>
                    {hostLocked && (
                      <span className="mt-1 block text-xs text-ink-subtle">{HOST_LOCKED_HINT}</span>
                    )}
                    <label className="mt-2 flex flex-col gap-1 text-sm">
                      <span className="text-ink-muted">
                        Shared secret
                        {authMoved
                          ? ` (${REENTER_HINT})`
                          : portal.has_radius_secret
                            ? " (leave blank to keep current)"
                            : ""}
                      </span>
                      <input
                        type="password"
                        value={radiusSecret}
                        onChange={(e) => setRadiusSecret(e.target.value)}
                        placeholder="RADIUS shared secret"
                        className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
                      />
                    </label>
                    </>)}
                  </div>
                )}

                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Walled garden (hosts reachable before login)</span>
                  <textarea
                    value={garden}
                    onChange={(e) => setGarden(e.target.value)}
                    rows={3}
                    placeholder="hotel.example.com&#10;cdn.example.com"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-sm text-ink outline-none focus:border-brand"
                  />
                </label>

                {/* Splash branding builder + live preview */}
                <div className="mt-1 rounded border border-line bg-surface-2 p-3">
                  <div className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                    Splash branding
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {/* fields */}
                    <div className="flex flex-col gap-2.5">
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-ink-muted">Headline</span>
                        <input value={headline} onChange={(e) => setHeadline(e.target.value)}
                          className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
                      </label>
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-ink-muted">Subhead</span>
                        <input value={subhead} onChange={(e) => setSubhead(e.target.value)}
                          className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
                      </label>
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-ink-muted">Logo URL (https or data:image)</span>
                        <input value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://…/logo.png"
                          className="rounded border border-line bg-surface px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand" />
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <ColorField label="Background" value={outerBg} onChange={setOuterBg} />
                        <ColorField label="Headline" value={headlineColor} onChange={setHeadlineColor} />
                        <ColorField label="Text" value={textColor} onChange={setTextColor} />
                        <ColorField label="Button" value={buttonColor} onChange={setButtonColor} />
                      </div>
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-ink-muted">Terms text (blank = no checkbox)</span>
                        <input value={termsText} onChange={(e) => setTermsText(e.target.value)}
                          className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
                      </label>
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-ink-muted">Redirect after login (optional)</span>
                        <input value={redirectUrl} onChange={(e) => setRedirectUrl(e.target.value)} placeholder="https://hotel.example.com/welcome"
                          className="rounded border border-line bg-surface px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand" />
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="text-ink-muted">Idle timeout (s)</span>
                          <input inputMode="numeric" value={idleTimeout}
                            onChange={(e) => setIdleTimeout(e.target.value.replace(/[^0-9]/g, ""))} placeholder="600"
                            className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="text-ink-muted">Session timeout (s)</span>
                          <input inputMode="numeric" value={sessionTimeout}
                            onChange={(e) => setSessionTimeout(e.target.value.replace(/[^0-9]/g, ""))} placeholder="7200"
                            className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
                        </label>
                      </div>
                    </div>

                    {/* live preview */}
                    <div>
                      <div className="mb-1 text-[0.625rem] uppercase tracking-wide text-ink-subtle">Live preview</div>
                      <div className="flex items-center justify-center rounded-lg p-4" style={{ background: outerBg }}>
                        <div className="w-full max-w-[220px]">
                          <div className="mb-3 text-center" style={{ color: headlineColor }}>
                            {logo.trim() && (
                              <img src={logo} alt="" className="mx-auto mb-2 max-h-8 object-contain"
                                onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                            )}
                            <div className="text-base font-semibold leading-tight">{headline || "Welcome"}</div>
                            <div className="text-[11px] opacity-85">{subhead}</div>
                          </div>
                          <div className="rounded-xl bg-white p-3 shadow-lg" style={{ color: textColor }}>
                            {mode !== "click" && (
                              <div className="mb-2 flex flex-col gap-1.5">
                                <div className="rounded border border-black/10 px-2 py-1.5 text-[10px] text-black/40">Username</div>
                                <div className="rounded border border-black/10 px-2 py-1.5 text-[10px] text-black/40">Password</div>
                              </div>
                            )}
                            {termsText.trim() && (
                              <div className="mb-2 flex items-start gap-1.5 text-[9px] opacity-80">
                                <input type="checkbox" disabled className="mt-0.5 scale-75" />
                                <span>{termsText}</span>
                              </div>
                            )}
                            <div className="rounded-md py-1.5 text-center text-[11px] font-semibold text-white"
                              style={{ background: buttonColor }}>
                              {mode === "click" ? "Connect" : "Sign in"}
                            </div>
                            <div className="mt-2 text-center text-[8px] opacity-50">Powered by Meridian</div>
                          </div>
                        </div>
                      </div>
                      <p className="mt-1.5 text-[0.625rem] text-ink-subtle">
                        Compiled to the AP splash (uCentral web-root) on save.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {set.isError && <span className="text-xs text-critical">Could not save the portal.</span>}
            {set.data && (
              <span className="text-xs text-healthy">Pushed to {set.data.pushed} AP{set.data.pushed === 1 ? "" : "s"}.</span>
            )}
            <div className="md-dialog-footer flex items-center justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={set.isPending}>
                {set.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
